export interface FilterMenuOption {
  value: string;
  label: string;
  group?: string;
}

export interface FilterMenuConfig {
  id: string;
  labelID: string;
  options: readonly FilterMenuOption[];
  value: string;
  describedBy?: string;
  disabled?: boolean;
  invalid?: boolean;
  onChange(value: string): void;
}

export interface FilterMenuHandle {
  setOptionLabel(value: string, label: string): void;
  setValue(value: string): boolean;
  setDescribedBy(value?: string): void;
  setDisabled(disabled: boolean): void;
  setInvalid(invalid: boolean): void;
  focus(): void;
  destroy(): void;
}

export type FilterMenuMove = "first" | "last" | "next" | "previous";

const typeaheadResetMilliseconds = 700;
const menuOpenEvent = "convoful:filter-menu-open";

export function movedFilterMenuIndex(
  length: number,
  current: number,
  move: FilterMenuMove,
): number {
  if (length <= 0) return -1;
  if (move === "first") return 0;
  if (move === "last") return length - 1;
  if (move === "next") return (Math.max(current, -1) + 1) % length;
  return (current <= 0 ? length : current) - 1;
}

export function matchingFilterMenuIndex(
  options: readonly FilterMenuOption[],
  query: string,
  current: number,
): number {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery || options.length === 0) return -1;
  for (let offset = 1; offset <= options.length; offset += 1) {
    const index = (Math.max(current, -1) + offset) % options.length;
    if (options[index]?.label.toLocaleLowerCase().startsWith(normalizedQuery)) {
      return index;
    }
  }
  return -1;
}

export function filterMenuOpensUpward(
  menuHeight: number,
  spaceAbove: number,
  spaceBelow: number,
): boolean {
  return menuHeight > spaceBelow && spaceAbove > spaceBelow;
}

function visibleVerticalBounds(element: HTMLElement): {
  top: number;
  bottom: number;
} {
  let top = 0;
  let bottom = window.innerHeight;
  for (
    let ancestor = element.parentElement;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    const { overflowY } = window.getComputedStyle(ancestor);
    if (!/(auto|scroll|hidden|clip)/.test(overflowY)) continue;
    const bounds = ancestor.getBoundingClientRect();
    top = Math.max(top, bounds.top);
    bottom = Math.min(bottom, bounds.bottom);
  }
  return { top, bottom };
}

function chevron(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.classList.add("filter-menu-chevron");
  svg.setAttribute("viewBox", "0 0 14 9");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M1.5 1.5 7 7l5.5-5.5");
  svg.append(path);
  return svg;
}

export function mountFilterMenu(
  root: HTMLElement,
  config: FilterMenuConfig,
): FilterMenuHandle {
  if (config.options.length === 0) {
    throw new Error("A filter menu needs at least one option.");
  }

  const abort = new AbortController();
  const { signal } = abort;
  const listboxID = `${config.id}-listbox`;
  const valueID = `${config.id}-value`;
  const options = config.options.map((option) => ({ ...option }));
  let selectedIndex = options.findIndex(({ value }) => (
    value === config.value
  ));
  if (selectedIndex < 0) selectedIndex = 0;
  let activeIndex = selectedIndex;
  let typeahead = "";
  let typeaheadTimeoutID: number | null = null;
  let open = false;

  root.className = "filter-menu";
  root.replaceChildren();

  const trigger = document.createElement("button");
  trigger.className = "filter-menu-trigger";
  trigger.id = config.id;
  trigger.type = "button";
  trigger.setAttribute("role", "combobox");
  trigger.setAttribute("aria-autocomplete", "none");
  trigger.setAttribute("aria-controls", listboxID);
  trigger.setAttribute("aria-expanded", "false");
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-labelledby", `${config.labelID} ${valueID}`);
  if (config.describedBy) {
    trigger.setAttribute("aria-describedby", config.describedBy);
  }
  if (config.invalid) trigger.setAttribute("aria-invalid", "true");
  trigger.disabled = config.disabled ?? false;

  const value = document.createElement("span");
  value.className = "filter-menu-value";
  value.id = valueID;
  value.textContent = options[selectedIndex]?.label ?? "";
  trigger.append(value, chevron());

  const listbox = document.createElement("div");
  listbox.className = "filter-menu-listbox";
  listbox.id = listboxID;
  listbox.hidden = true;
  listbox.setAttribute("role", "listbox");
  const visibleLabel = document.getElementById(config.labelID)?.textContent
    ?.trim();
  listbox.setAttribute(
    "aria-label",
    visibleLabel ? `${visibleLabel} options` : "Filter options",
  );

  const optionLabels = new Map<string, HTMLSpanElement>();
  const optionGroups = new Map<string, HTMLElement>();
  const optionElements = options.map((item, index) => {
    const element = document.createElement("div");
    element.className = "filter-menu-option";
    element.id = `${config.id}-option-${index}`;
    element.dataset.value = item.value;
    element.setAttribute("role", "option");
    element.setAttribute("aria-selected", String(index === selectedIndex));

    const check = document.createElement("span");
    check.className = "filter-menu-check";
    check.setAttribute("aria-hidden", "true");
    check.textContent = "✓";

    const label = document.createElement("span");
    label.textContent = item.label;
    optionLabels.set(item.value, label);
    element.append(check, label);
    if (item.group) {
      let group = optionGroups.get(item.group);
      if (!group) {
        const groupLabel = document.createElement("span");
        groupLabel.className = "filter-menu-group-label";
        groupLabel.id = `${config.id}-group-${optionGroups.size}`;
        groupLabel.textContent = item.group;
        group = document.createElement("div");
        group.className = "filter-menu-group";
        group.setAttribute("role", "group");
        group.setAttribute("aria-labelledby", groupLabel.id);
        group.append(groupLabel);
        optionGroups.set(item.group, group);
        listbox.append(group);
      }
      group.append(element);
    } else {
      listbox.append(element);
    }
    return element;
  });

  root.append(trigger, listbox);

  const clearTypeahead = () => {
    typeahead = "";
    if (typeaheadTimeoutID !== null) {
      window.clearTimeout(typeaheadTimeoutID);
      typeaheadTimeoutID = null;
    }
  };

  const setActive = (index: number) => {
    if (index < 0 || index >= optionElements.length) return;
    activeIndex = index;
    optionElements.forEach((element, optionIndex) => {
      element.classList.toggle("is-active", optionIndex === activeIndex);
    });
    trigger.setAttribute(
      "aria-activedescendant",
      optionElements[activeIndex]?.id ?? "",
    );
    optionElements[activeIndex]?.scrollIntoView({ block: "nearest" });
  };

  const close = () => {
    if (!open) return;
    open = false;
    root.classList.remove("is-open");
    trigger.setAttribute("aria-expanded", "false");
    trigger.removeAttribute("aria-activedescendant");
    listbox.hidden = true;
    optionElements.forEach((element) => {
      element.classList.remove("is-active");
    });
    clearTypeahead();
  };

  const positionListbox = () => {
    const triggerBounds = trigger.getBoundingClientRect();
    const visibleBounds = visibleVerticalBounds(root);
    root.classList.toggle(
      "opens-upward",
      filterMenuOpensUpward(
        listbox.getBoundingClientRect().height,
        triggerBounds.top - visibleBounds.top,
        visibleBounds.bottom - triggerBounds.bottom,
      ),
    );
  };

  const openMenu = (requestedIndex = selectedIndex) => {
    if (trigger.disabled) return;
    if (open) {
      setActive(requestedIndex);
      return;
    }
    document.dispatchEvent(new CustomEvent(menuOpenEvent, { detail: root }));
    open = true;
    root.classList.add("is-open");
    trigger.setAttribute("aria-expanded", "true");
    listbox.hidden = false;
    positionListbox();
    setActive(requestedIndex);
  };

  const setSelected = (index: number) => {
    const next = options[index];
    if (!next) return false;
    selectedIndex = index;
    activeIndex = index;
    value.textContent = next.label;
    optionElements.forEach((element, optionIndex) => {
      element.setAttribute(
        "aria-selected",
        String(optionIndex === selectedIndex),
      );
    });
    return true;
  };

  const commit = (index: number) => {
    const next = options[index];
    if (!next) return;
    const changed = index !== selectedIndex;
    setSelected(index);
    close();
    trigger.focus({ preventScroll: true });
    if (changed) config.onChange(next.value);
  };

  trigger.addEventListener("click", () => {
    if (open) close();
    else openMenu();
  }, { signal });

  trigger.addEventListener("keydown", (event) => {
    const move = (
      event.key === "ArrowDown" ? "next"
        : event.key === "ArrowUp" ? "previous"
          : event.key === "Home" ? "first"
            : event.key === "End" ? "last"
              : null
    ) satisfies FilterMenuMove | null;
    if (move) {
      event.preventDefault();
      event.stopPropagation();
      if (!open) {
        openMenu(selectedIndex);
        return;
      }
      setActive(movedFilterMenuIndex(options.length, activeIndex, move));
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      if (open) commit(activeIndex);
      else openMenu();
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      close();
      trigger.focus({ preventScroll: true });
      return;
    }
    if (event.key === "Tab") {
      close();
      return;
    }
    if (
      event.key.length !== 1
      || event.ctrlKey
      || event.metaKey
      || event.altKey
    ) return;
    event.preventDefault();
    event.stopPropagation();
    typeahead += event.key.toLocaleLowerCase();
    if (typeaheadTimeoutID !== null) {
      window.clearTimeout(typeaheadTimeoutID);
    }
    typeaheadTimeoutID = window.setTimeout(
      clearTypeahead,
      typeaheadResetMilliseconds,
    );
    if (!open) openMenu(selectedIndex);
    const match = matchingFilterMenuIndex(
      options,
      typeahead,
      activeIndex,
    );
    if (match >= 0) setActive(match);
  }, { signal });

  optionElements.forEach((element, index) => {
    element.addEventListener("pointerdown", (event) => {
      event.preventDefault();
    }, { signal });
    element.addEventListener("click", () => commit(index), { signal });
    element.addEventListener("pointermove", (event) => {
      if (event.pointerType !== "touch") setActive(index);
    }, { signal });
  });

  document.addEventListener("pointerdown", (event) => {
    if (open && !root.contains(event.target as Node | null)) close();
  }, { capture: true, signal });

  document.addEventListener(menuOpenEvent, (event) => {
    if ((event as CustomEvent<HTMLElement>).detail !== root) close();
  }, { signal });

  window.addEventListener("blur", close, { signal });
  window.addEventListener("resize", () => {
    if (open) positionListbox();
  }, { signal });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) close();
  }, { signal });

  return {
    setOptionLabel(optionValue: string, label: string): void {
      const index = options.findIndex(({ value }) => value === optionValue);
      if (index < 0) return;
      options[index] = { ...options[index], label };
      const optionLabel = optionLabels.get(optionValue);
      if (optionLabel) optionLabel.textContent = label;
      if (index === selectedIndex) value.textContent = label;
    },
    setValue(optionValue: string): boolean {
      const index = options.findIndex(({ value: candidate }) => (
        candidate === optionValue
      ));
      if (index < 0) return false;
      setSelected(index);
      close();
      return true;
    },
    setDescribedBy(descriptionIDs?: string): void {
      if (descriptionIDs) {
        trigger.setAttribute("aria-describedby", descriptionIDs);
      } else {
        trigger.removeAttribute("aria-describedby");
      }
    },
    setDisabled(disabled: boolean): void {
      trigger.disabled = disabled;
      if (disabled) close();
    },
    setInvalid(invalid: boolean): void {
      if (invalid) trigger.setAttribute("aria-invalid", "true");
      else trigger.removeAttribute("aria-invalid");
    },
    focus(): void {
      trigger.focus({ preventScroll: true });
    },
    destroy(): void {
      close();
      clearTypeahead();
      abort.abort();
    },
  };
}
